import React, { useMemo } from 'react';
import { Text, H4, Label } from '@amzn/stencil-react-components/text';
import { useTranslation } from 'react-i18next';
import { Flex, Spacer, Col, Hr } from '@amzn/stencil-react-components/layout';
import { CardStatus, Card } from '@amzn/stencil-react-components/card';
import { Stat, StatType } from '@amzn/stencil-react-components/data-visualization';
import { useUserDataContext } from '../../hooks';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { FeedbackParam } from '../../types/backendService';
import { FEEDBACK_MONTHS_LOOKBACK } from '../../constants/feedbackConfiguration';
import { titleizeString } from '../../util/string_utils';
import { colorForFeedback } from '../../util/feedback_styling';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';
import { generateFeedbackDocumentFields } from '../../util/feedback_document_utils';

const IconWithTooltip = withTooltip()(IconQuestionCircle);

interface FeedbackLoaderProps {
  feedbackCategories: string[]; // feedback categories to retrieve from the backend. e.g. ['Safety']
  subtitle: string; // subtitle that describes the types of feedbacks being displayed
}

export const FeedbackLoader = (props: FeedbackLoaderProps) => {
  const { t } = useTranslation();
  const { feedbacks } = useUserDataContext();

  const filteredFeedbacks: FeedbackParam[]|undefined = (feedbacks || [])
      .filter((feedback: FeedbackParam) => props.feedbackCategories.includes(feedback.feedbackCategory!));
  const loading = (feedbacks == undefined);

  const feedbackLevelTooltips: Record<string, string> = {
    "INFO": t('infoFeedbackExplanation'),
    "VERBAL_COACHING": t('verbalCoachingFeedbackExplanation'),
    "DOCUMENTED_COACHING": t('documentedCoachingFeedbackExplanation'),
    "FIRST_WRITTEN_WARNING": t('writtenWarningFeedbackExplanation'),
    "SECOND_WRITTEN_WARNING": t('writtenWarningFeedbackExplanation'),
    "FINAL_WRITTEN_WARNING": t('writtenWarningFeedbackExplanation'),
  };

  const feedbackLevelLabels: Record<string, string> = {
    "INFO": t('info'),
    "VERBAL_COACHING": t('verbalCoaching'),
    "DOCUMENTED_COACHING": t('documentedCoaching'),
    "FIRST_WRITTEN_WARNING": t('firstWrittenWarning'),
    "SECOND_WRITTEN_WARNING": t('secondWrittenWarning'),
    "FINAL_WRITTEN_WARNING": t('finalWrittenWarning'),
  };

  const feedbackContent = useMemo(() => {
    return filteredFeedbacks
        .sort((a, b) => b.deliveredDateUtc! - a.deliveredDateUtc!)
        .map((feedback: FeedbackParam, index: number) => {
      let feedbackText = [];

      let feedbackDocumentFields = generateFeedbackDocumentFields(feedback, t)!;

      if (feedbackDocumentFields.summary != undefined && feedbackDocumentFields.summary.trim() != '') {
        feedbackText.push(
          <>
            <Spacer height={16} />
            <Col>
              <Label>{t('summary')}</Label>
              <Hr />
              <Text fontWeight="light">
                {feedbackDocumentFields.summary}
              </Text>
            </Col>
          </>
        );
      }
      if (feedbackDocumentFields.incidentDetails != undefined && feedbackDocumentFields.incidentDetails.trim() != '') {
        feedbackText.push(
          <>
            <Spacer height={16} />
            <Col>
              <Label>{t('incidentDetails')}</Label>
              <Hr />
              <Text fontWeight="light">
                {feedbackDocumentFields.incidentDetails}
              </Text>
            </Col>
          </>
        );
      }
      if (feedbackDocumentFields.areasOfImprovement != undefined && feedbackDocumentFields.areasOfImprovement.trim() != '') {
        feedbackText.push(
          <>
            <Spacer height={16} />
            <Col>
              <Label>{t('areasOfImprovement')}</Label>
              <Hr />
              <Text fontWeight="light">
                {feedbackDocumentFields.areasOfImprovement}
              </Text>
            </Col>
          </>
        );
      }

      return (
        <Card data-cy="feedback-card" key={index}>
          <Col width="100%">
            <Flex flexDirection="row" justifyContent="space-between" width="100%">
              <H4 color={colorForFeedback(feedback)}>{feedback.warehouseId?.toUpperCase()} - {titleizeString(feedback.type!)}</H4>
              <Flex flexDirection="row" justifyContent="center" alignItems="center">
                <IconWithTooltip tooltipText={feedbackLevelTooltips[feedback.level!]} />
                <Text fontWeight="bold">
                  {feedbackLevelLabels[feedback.level!]?.toUpperCase() || feedback.level!.split('_').join(' ')}
                </Text>
              </Flex>
            </Flex>
            <Text fontWeight="light">
              <em>
                {t('deliveredByText', {
                  user: `${feedback.deliveredBy}@`,
                  date: new Date(feedback.deliveredDateUtc! * 1000).toLocaleDateString(),
                  interpolation: {'escapeValue': false},
                })}
              </em>
            </Text>
            {feedbackText}
          </Col>
        </Card>
      );
    })
  }, [feedbacks]);

  return(
    <>
      {loading &&
        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          <Spinner showText loadingText={t('loadingFeedbacks')} />
        </Flex>
      }
      {!loading &&
        <>
          <Card status={CardStatus.Default}>
            <Flex flexDirection="row" justifyContent={"center"} width="100%" alignItems="center">
              <Stat
                dataTestId="feedback-stat"
                type={StatType.Large}
                numeralText={filteredFeedbacks.length.toString()}
                descriptionText={t('feedbacksDataTitle', { months: FEEDBACK_MONTHS_LOOKBACK })}
              />
            </Flex>
          </Card>
          {filteredFeedbacks.length > 0 &&
            <>
              <Spacer height={16}/>

              <Text fontWeight="light">
                {props.subtitle}
              </Text>

              <Spacer height={16}/>

              <Col gridGap="S300" >
                {feedbackContent}
              </Col>
            </>
          }
          {filteredFeedbacks.length == 0 &&
            <Text fontWeight="light">{`${t('feedbackTooltip')} ${t('yesterdaysDataTooltip')}`}</Text>
          }
        </>
      }
    </>
  );
}
